import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useSubscription } from "@/hooks/useSubscription";
import { usePendingArchives } from "@/hooks/usePendingArchives";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, AlertTriangle, Trash2 } from "lucide-react";
import { AppSeo } from "@/components/AppSeo";
import { useDowngradeReviewData } from "@/components/downgrade/useDowngradeReviewData";
import { useDowngradeActions } from "@/components/downgrade/useDowngradeActions";
import { AtRiskItemsCard } from "@/components/downgrade/AtRiskItemsCard";
import { AtRiskExcerptsCard } from "@/components/downgrade/AtRiskExcerptsCard";

const labelFor = (w: { workspace_label: string | null; display_name: string | null }) =>
  w.workspace_label || w.display_name || "Workspace";

const DowngradeReview = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { workspaces, refresh: refreshWs } = useWorkspace();
  const { hasPendingDowngrade, pendingEffectiveAt, refresh: refreshSub } = useSubscription();
  const { archives, refresh: refreshArchives, loading: archivesLoading } = usePendingArchives();

  const doomedWorkspaceIds = useMemo(() => archives.map((a) => a.workspace_id), [archives]);
  const doomedSet = useMemo(() => new Set(doomedWorkspaceIds), [doomedWorkspaceIds]);
  const survivingWorkspaces = useMemo(
    () => workspaces.filter((w) => !doomedSet.has(w.id)),
    [workspaces, doomedSet],
  );

  const { items, setItems, excerpts, setExcerpts, loading, reload } = useDowngradeReviewData({
    doomedWorkspaceIds,
    doomedSet,
    workspaces,
  });

  const actions = useDowngradeActions({
    items,
    setItems,
    excerpts,
    setExcerpts,
    reload,
    survivingWorkspaces,
    doomedWorkspaceIds,
    refreshArchives,
    refreshSub,
    refreshWs,
  });

  const primary = workspaces.find((w) => w.is_primary);
  const isPlanOwner = !!primary && !!user && primary.owner_user_id === user.id;
  const cutoverDate = pendingEffectiveAt ? pendingEffectiveAt.toLocaleDateString() : "the cutover date";

  if (!archivesLoading && !hasPendingDowngrade && doomedWorkspaceIds.length === 0) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <h1 className="font-serif text-3xl font-semibold">Downgrade review</h1>
        <Card className="shadow-soft">
          <CardContent className="space-y-3 py-12 text-center">
            <p className="text-muted-foreground">You have no pending plan changes that require review.</p>
            <Button asChild variant="outline">
              <Link to="/settings"><ArrowLeft className="h-4 w-4" /> Back to settings</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isPlanOwner) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <h1 className="font-serif text-3xl font-semibold">Downgrade review</h1>
        <Card className="shadow-soft border-destructive/40">
          <CardContent className="space-y-3 py-8">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <p className="font-medium">Plan owner only</p>
            </div>
            <p className="text-sm text-muted-foreground">
              Only the plan owner can review and confirm what happens to library items in workspaces scheduled for archive.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
    <AppSeo title="Review downgrade" />
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="space-y-1">
        <Button asChild variant="ghost" size="sm" className="-ml-3">
          <Link to="/settings"><ArrowLeft className="h-4 w-4" /> Back to settings</Link>
        </Button>
        <h1 className="font-serif text-3xl font-semibold">Downgrade review</h1>
        <p className="text-muted-foreground">
          On <strong>{cutoverDate}</strong>, the workspaces below will be archived. Items only visible in those workspaces will be lost unless you move them now.
        </p>
      </div>

      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle className="text-base">Workspaces being archived</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {archives.length === 0 ? (
              <span className="text-sm text-muted-foreground">None.</span>
            ) : (
              archives.map((a) => {
                const w = workspaces.find((ws) => ws.id === a.workspace_id);
                return (
                  <Badge key={a.id} variant="secondary">
                    {w ? labelFor(w) : a.workspace_id.slice(0, 8)}
                  </Badge>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      <AtRiskItemsCard
        loading={loading}
        items={items}
        workspaces={workspaces}
        survivingWorkspaces={survivingWorkspaces}
        selected={actions.selected}
        bulkTarget={actions.bulkTarget}
        busy={actions.busy}
        cutoverDate={cutoverDate}
        onToggleOne={actions.toggleOne}
        onToggleAll={actions.toggleAll}
        onBulkTargetChange={actions.setBulkTarget}
        onBulkMove={actions.bulkMove}
        onBulkDelete={actions.bulkDelete}
        onMoveOne={actions.moveOne}
        onDeleteOne={actions.deleteOne}
      />

      <AtRiskExcerptsCard
        loading={loading}
        excerpts={excerpts}
        workspaces={workspaces}
        selectedExcerpts={actions.selectedExcerpts}
        busy={actions.busy}
        cutoverDate={cutoverDate}
        onToggleExcerpt={actions.toggleExcerpt}
        onToggleAll={actions.toggleAllExcerpts}
        onBulkDelete={actions.bulkDeleteExcerpts}
        onDeleteOne={actions.deleteOneExcerpt}
      />

      <Card className="shadow-soft border-destructive/30">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Trash2 className="h-4 w-4 text-destructive" />
            Delete archived workspaces now
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Skip the wait until {cutoverDate} and permanently delete the {doomedWorkspaceIds.length} affected workspace{doomedWorkspaceIds.length === 1 ? "" : "s"} right now. This cannot be undone. Triage every at-risk library item first — the button stays disabled until the list above is empty.
          </p>
          <div className="flex justify-end">
            <Button
              variant="destructive"
              size="sm"
              onClick={actions.purgeNow}
              disabled={actions.purging || items.length > 0 || doomedWorkspaceIds.length === 0}
            >
              {actions.purging ? "Deleting…" : `Delete ${doomedWorkspaceIds.length} workspace${doomedWorkspaceIds.length === 1 ? "" : "s"} now`}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          variant="outline"
          onClick={async () => {
            await Promise.all([refreshArchives(), refreshSub(), refreshWs()]);
            navigate("/settings");
          }}
        >
          Done for now
        </Button>
      </div>
    </div>
    </>
  );
};

export default DowngradeReview;
